import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, RefreshCwIcon, CreditCardIcon, UserIcon, BuildingIcon, FileTextIcon } from 'lucide-react';
import { Sale } from '../types/Transaction';
import { RefundModal } from '../components/RefundModal';
import { TransactionFinancialBreakdown } from '../components/TransactionFinancialBreakdown';
import { SCENARIOS, buildScenario } from '../utils/transactionScenarios';
import { getMockSaleById, getSaleFixture } from '../utils/mockSales';
import { computeTransactionViewModel } from '../utils/transactionViewModel';
import { formatMoney } from '../utils/money';
export function SaleDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  // Get sale fixture and initialize scenario from it
  const saleFixture = getSaleFixture(id || 'payment-with-reserve');
  const [selectedScenarioId, setSelectedScenarioId] = useState(saleFixture?.defaultScenarioId || 'payment-with-reserve');
  // Get base sale data from shared fixtures
  const baseSale = getMockSaleById(id || 'payment-with-reserve');
  // Compute current scenario data (updates when scenario changes)
  const currentScenario = useMemo(() => {
    return buildScenario(selectedScenarioId);
  }, [selectedScenarioId]);
  const currentViewModel = useMemo(() => {
    return computeTransactionViewModel(currentScenario.transaction, currentScenario.ledgerRows, 'included');
  }, [currentScenario]);
  // Build sale object with current scenario data
  const sale: Sale = useMemo(() => {
    if (!baseSale) {
      // Fallback if sale not found
      return {
        id: id || 'unknown',
        transactionId: currentScenario.transaction.transactionId,
        amount: currentViewModel.headerAmount,
        currency: currentScenario.transaction.currency,
        status: 'completed',
        source: 'payment_link',
        date: currentScenario.transaction.date,
        customerName: 'Unknown Customer',
        customerEmail: 'unknown@example.com',
        paymentMethod: {
          type: 'card'
        },
        merchantName: 'Unknown Merchant',
        merchantId: 'MERCH-000',
        description: currentScenario.description,
        fee: currentViewModel.summary.feesPostedDisplay,
        netAmount: currentViewModel.summary.netPosted,
        lifecycle: []
      };
    }
    // Override transaction data with current scenario
    return {
      ...baseSale,
      transactionId: currentScenario.transaction.transactionId,
      amount: currentViewModel.headerAmount,
      currency: currentScenario.transaction.currency,
      date: currentScenario.transaction.date,
      fee: currentViewModel.summary.feesPostedDisplay,
      netAmount: currentViewModel.summary.netPosted
    };
  }, [baseSale, currentScenario, currentViewModel, id]);
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      initiated: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      refund_initiated: 'bg-orange-100 text-orange-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-10531">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const handleRefund = (amount: number, reason: string, notes: string) => {
    console.log('Refund initiated:', {
      transactionId: sale.transactionId,
      amount,
      reason,
      notes
    });
    // In a real app, this would call an API to process the refund
    alert(`Refund of ${formatMoney(amount, sale.currency)} initiated successfully!`);
  };
  return <div className="space-y-6" data-id="element-10532">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-10533">
        <button onClick={() => navigate('/sales')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-10534">
          <ArrowLeftIcon size={20} data-id="element-10535" />
          Back to Sales
        </button>
        <div className="flex items-center gap-3" data-id="element-10536">
          {/* Dev-only scenario selector */}
          <select value={selectedScenarioId} onChange={e => setSelectedScenarioId(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50" data-id="element-10537">
            {SCENARIOS.map(scenario => <option key={scenario.scenarioId} value={scenario.scenarioId} data-id="element-10538">
                {scenario.description}
              </option>)}
          </select>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-10539">
            <DownloadIcon size={18} data-id="element-10540" />
            Download Receipt
          </button>
          {sale.status === 'completed' && <button onClick={() => setRefundModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-10541">
              <RefreshCwIcon size={18} data-id="element-10542" />
              Refund
            </button>}
        </div>
      </div>
      {/* Transaction Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-10543">
        <div className="flex items-start justify-between mb-6" data-id="element-10544">
          <div data-id="element-10545">
            <div className="text-4xl font-bold text-gray-900" data-id="element-10546">
              {formatMoney(sale.amount, sale.currency)}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mt-2" data-id="element-10547">
              {sale.transactionId}
            </h1>
          </div>
          <div data-id="element-10548">{getStatusBadge(sale.status)}</div>
        </div>

        {/* Transaction Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10549">
          {/* Customer Information */}
          <div className="space-y-4" data-id="element-10550">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-10551">
              <UserIcon size={20} className="text-blue-600" data-id="element-10552" />
              Customer Information
            </h3>
            <div className="space-y-3" data-id="element-10553">
              <div data-id="element-10554">
                <div className="text-sm text-gray-500" data-id="element-10555">Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-10556">
                  {sale.customerName}
                </div>
              </div>
              <div data-id="element-10557">
                <div className="text-sm text-gray-500" data-id="element-10558">Email</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-10559">
                  {sale.customerEmail}
                </div>
              </div>
              {sale.customerPhone && <div data-id="element-10560">
                  <div className="text-sm text-gray-500" data-id="element-10561">Phone</div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-10562">
                    {sale.customerPhone}
                  </div>
                </div>}
            </div>
          </div>
          {/* Payment Information */}
          <div className="space-y-4" data-id="element-10563">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-10564">
              <CreditCardIcon size={20} className="text-blue-600" data-id="element-10565" />
              Payment Information
            </h3>
            <div className="space-y-3" data-id="element-10566">
              <div data-id="element-10567">
                <div className="text-sm text-gray-500" data-id="element-10568">Payment Method</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-10569">
                  {sale.paymentMethod.brand || sale.paymentMethod.type}{' '}
                  {sale.paymentMethod.last4 && `•••• ${sale.paymentMethod.last4}`}
                  {sale.paymentMethod.details && sale.paymentMethod.details}
                </div>
              </div>
              <div data-id="element-10570">
                <div className="text-sm text-gray-500" data-id="element-10571">Transaction Date</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-10572">
                  {new Date(sale.date).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                </div>
              </div>
              <div data-id="element-10573">
                <div className="text-sm text-gray-500" data-id="element-10574">Source</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-10575">
                  {sale.source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </div>
              </div>
            </div>
          </div>
          {/* Merchant Information */}
          <div className="space-y-4" data-id="element-10576">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-10577">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-10578" />
              Merchant Information
            </h3>
            <div className="space-y-3" data-id="element-10579">
              <div data-id="element-10580">
                <div className="text-sm text-gray-500" data-id="element-10581">Merchant Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-10582">
                  {sale.merchantName}
                </div>
              </div>
              <div data-id="element-10583">
                <div className="text-sm text-gray-500" data-id="element-10584">Merchant ID</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-10585">
                  {sale.merchantId}
                </div>
              </div>
            </div>
          </div>
          {/* Additional Details */}
          {sale.invoiceId && <div className="space-y-4" data-id="element-10586">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-10587">
                <FileTextIcon size={20} className="text-blue-600" data-id="element-10588" />
                Related Documents
              </h3>
              <div className="space-y-3" data-id="element-10589">
                <div data-id="element-10590">
                  <div className="text-sm text-gray-500" data-id="element-10591">Invoice</div>
                  <button onClick={() => navigate(`/invoice-detail/${sale.invoiceId}`)} className="text-sm font-medium text-blue-600 hover:underline" data-id="element-10592">
                    {sale.invoiceId}
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* New Financial Breakdown */}
      <TransactionFinancialBreakdown transactionId={sale.transactionId} scenarioId={selectedScenarioId} currency={sale.currency} data-id="element-10593" />

      {/* Transaction Lifecycle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-10594">
        <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-10595">
          Transaction Lifecycle
        </h2>
        <div className="space-y-6" data-id="element-10596">
          {currentScenario.ledgerRows.filter(row => row.status === 'Posted').map((row, index, filteredRows) => <div key={row.id} className="relative" data-id="element-10597">
                {index !== filteredRows.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-10598"></div>}
                <div className="flex items-start gap-4" data-id="element-10599">
                  <div className="flex-shrink-0" data-id="element-10600">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-10601">
                      <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-10602"></div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-10603">
                    <div className="flex items-center justify-between" data-id="element-10604">
                      <div className="flex items-center gap-3" data-id="element-10605">
                        <p className="text-sm font-medium text-gray-900" data-id="element-10606">
                          {row.posting}
                        </p>
                        {getStatusBadge(row.status.toLowerCase())}
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-10607">{row.effectiveAt}</p>
                    </div>
                  </div>
                </div>
              </div>)}
        </div>
      </div>

      {/* Refund Modal */}
      <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} transactionAmount={sale.amount} transactionId={sale.transactionId} currency="RM" onRefund={handleRefund} data-id="element-10608" />
    </div>;
}